import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

/**
 * Ad-category updates a campaign still has to answer.
 *
 * When an admin edits ad-category content (wizard steps 1–4) that a campaign is
 * using, the campaign stays on the version it started with until someone with
 * the `decide_ad_category_versions` permission chooses to keep it or move to the
 * newer version.
 */

export interface VersionUpdateChange {
  field: string;
  from: string;
  to: string;
}

export interface VersionUpdate {
  entity_type: string;
  entity_id: string;
  from_version: number;
  to_version: number;
  /** e.g. "Dimensions (W X H)" or the category / ad type name. */
  label: string;
  /** Where it sits, e.g. "Social – Meta › Instagram › Instagram Stories 9:16". */
  context: string;
  archived: boolean;
  changes: VersionUpdateChange[];
}

export type VersionChoice = "keep" | "adopt";

interface VersionUpdatesResponse {
  can_decide: boolean;
  updates: VersionUpdate[];
}

interface VersionUpdatesState {
  campaignId: string | null;
  updates: VersionUpdate[];
  canDecide: boolean;
  isLoading: boolean;
  /** Key of the update currently being answered, to disable its buttons. */
  decidingKey: string | null;
  fetchVersionUpdates: (campaignId: string) => Promise<void>;
  decide: (
    campaignId: string,
    update: VersionUpdate,
    choice: VersionChoice,
  ) => Promise<void>;
}

export const versionUpdateKey = (update: VersionUpdate) =>
  `${update.entity_type}:${update.entity_id}:${update.from_version}:${update.to_version}`;

const errorMessage = (error: unknown, fallback: string) => {
  const axiosError = error as AxiosError<any>;
  return (
    axiosError.response?.data?.message ??
    axiosError.response?.data?.error ??
    fallback
  );
};

const useVersionUpdatesStore = create<VersionUpdatesState>((set, get) => ({
  campaignId: null,
  updates: [],
  canDecide: false,
  isLoading: false,
  decidingKey: null,

  fetchVersionUpdates: async (campaignId: string) => {
    // Don't show another campaign's updates while this one loads.
    if (get().campaignId !== campaignId) {
      set({ campaignId, updates: [], canDecide: false });
    }
    set({ isLoading: true });
    try {
      const response = await apiClient.get(
        `/campaigns/${campaignId}/version-updates`,
      );
      const data: VersionUpdatesResponse = response.data.data;
      set({
        updates: data.updates ?? [],
        canDecide: Boolean(data.can_decide),
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw new Error(errorMessage(error, "Failed to load ad category updates"));
    }
  },

  decide: async (campaignId, update, choice) => {
    const key = versionUpdateKey(update);
    set({ decidingKey: key });
    try {
      await apiClient.post("/ad-categories/version-decisions", {
        campaign_id: campaignId,
        entity_type: update.entity_type,
        entity_id: update.entity_id,
        from_version: update.from_version,
        to_version: update.to_version,
        choice,
      });
      set((state) => ({
        updates: state.updates.filter((u) => versionUpdateKey(u) !== key),
        decidingKey: null,
      }));
    } catch (error) {
      set({ decidingKey: null });
      throw new Error(errorMessage(error, "Failed to save your choice"));
    }
  },
}));

export default useVersionUpdatesStore;
